import {
  Component,
  createRef,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  ChangeEventHandler
} from 'react';
import { InputOptionAttributes } from 'components/Shared/Form/InputSelect/option';
import { InputProps } from 'components/Shared/Form/input';
import { Input } from 'components/Shared/Form/themes/BGH/input';
import { LabeledInput } from '../LabeledInput/themes/BGH';
import cn from 'classnames';
import styles from './select.scss';
import {
  defaultComponents,
  SelectComponents
} from 'components/Shared/Form/InputSelect/defaultComponents';
import { NO_ELEMENT } from 'utils';

export interface InputSelectProps extends Omit<InputProps, 'onSelect' | 'onChange'> {
  components?: SelectComponents;
  options?: InputOptionAttributes[];
  pageSize?: number;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onSelect: (value: InputOptionAttributes) => void;
  onMenuOpen: () => void;
  onMenuClose: () => void;
  isMenuOpen: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  inputClassName?: string;
  useLabeledInput?: true;
  value: number | string;
}

interface State {
  isFocused: boolean;
  focusedOption?: InputOptionAttributes;
  selectValue?: InputOptionAttributes;
}

export class InputSelect extends Component<InputSelectProps, State> {
  public state: State = {
    isFocused: false,
    focusedOption: undefined,
    selectValue: undefined
  };
  private PAGE_SIZE_DEFAULT = 5;
  private inputRef = createRef<HTMLInputElement>();
  private focusedOptionRef = createRef<HTMLElement>();
  private components: SelectComponents;
  constructor(props: InputSelectProps) {
    super(props);
    this.setComponents(props.components);
  }

  public componentDidMount() {
    if (document && document.addEventListener) {
      document.addEventListener('scroll', this.onScroll, true);
    }
    if (this.props.autoFocus) {
      this.focusInput();
    }
  }
  public componentWillUnmount() {
    document.removeEventListener('scroll', this.onScroll, true);
  }

  public render() {
    const {
      className,
      inputClassName,
      useLabeledInput,
      isMenuOpen,
      isDisabled,
      isLoading,
      pageSize,
      onMenuClose,
      onMenuOpen,
      components,
      options,
      onSelect,
      onChange,
      ...inputProps
    } = this.props;
    const input = {
      ...inputProps,
      onChange: this.handleInputChange,
      onFocus: this.handleInputFocus,
      onBlur: this.handleInputBlur,
      onKeyDown: this.onKeyDown
    };
    return (
      <div className={cn(styles.wrapper, className)}>
        {useLabeledInput ? (
          <LabeledInput innerRef={this.inputRef} className={inputClassName} {...input} />
        ) : (
          <Input innerRef={this.inputRef} {...input} />
        )}
        {this.renderOptions()}
      </div>
    );
  }

  private renderOptions = () => {
    if (
      !this.props.isMenuOpen ||
      !(this.props.isLoading || (this.props.options && this.props.options.length))
    ) {
      return NO_ELEMENT;
    }
    const { LoadingOption, MenuWrapper, Option } = this.components;
    const { focusedOption } = this.state;
    const render = (props: InputOptionAttributes) => {
      const onSelect = () => this.selectOption(props);
      const onHover = () => this.hoverOption(props);
      const focused = focusedOption === props;
      const selected = props.value === this.props.value;
      const ref = focused ? this.focusedOptionRef : undefined;
      return (
        <Option
          innerRef={ref}
          {...props}
          focused={focused}
          selected={selected}
          onHover={onHover}
          onSelect={onSelect}
          onMouseDown={this.onMouseDown}
          label={props.label}
        />
      );
    };
    return (
      <MenuWrapper>
        {this.props.isLoading ? (
          <LoadingOption />
        ) : (
          this.props.options && this.props.options.map(render)
        )}
      </MenuWrapper>
    );
  };

  private focusInput = () => {
    if (!this.inputRef.current) {
      return;
    }
    this.inputRef.current.focus();
  };
  private blurInput = () => {
    if (!this.inputRef.current) {
      return;
    }
    this.inputRef.current.blur();
  };

  private handleInputChange: ChangeEventHandler<HTMLInputElement> = e => {
    this.props.onChange(e);
    this.props.onMenuOpen();
  };
  private handleInputFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
    this.props.onMenuOpen();
    this.setState({
      isFocused: true
    });
  };
  private handleInputBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
    this.props.onMenuClose();
    this.setState({
      focusedOption: undefined,
      isFocused: false
    });
  };

  private onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (typeof this.props.onKeyDown === 'function') {
      this.props.onKeyDown(e);
      if (e.defaultPrevented) {
        return;
      }
    }
    const {
      props: { isMenuOpen },
      state: { focusedOption }
    } = this;
    if (e.key === 'PageUp' && isMenuOpen) {
      this.focusOption('pageUp');
    } else if (e.key === 'PageDown' && isMenuOpen) {
      this.focusOption('pageDown');
    } else if (e.key === 'Enter' && isMenuOpen && focusedOption) {
      this.selectOption(focusedOption);
      this.props.onMenuClose();
    } else if (e.key === 'ArrowUp') {
      if (isMenuOpen) {
        this.focusOption('prev');
      } else {
        this.openMenu('last');
      }
    } else if (e.key === 'ArrowDown') {
      if (isMenuOpen) {
        this.focusOption('next');
      } else {
        this.openMenu('first');
      }
    } else if (e.key === 'Escape' && isMenuOpen) {
      this.closeMenu();
    } else {
      return;
    }
    e.preventDefault();
  };

  private openMenu = (direction: 'last' | 'first') => {
    this.props.onMenuOpen();
    this.focusOption(direction);
  };

  private closeMenu = () => {
    this.props.onMenuClose();
  };

  private focusOption = (direction: 'first' | 'last' | 'prev' | 'next' | 'pageUp' | 'pageDown') => {
    const { options, pageSize = this.PAGE_SIZE_DEFAULT } = this.props;
    if (!options || !options.length) {
      return;
    }
    const { focusedOption } = this.state;
    let nextIndex = 0;
    const focusedIndex = focusedOption ? options.indexOf(focusedOption) : -1;

    if (direction === 'prev') {
      nextIndex = focusedIndex > 0 ? focusedIndex - 1 : options.length - 1;
    } else if (direction === 'next') {
      nextIndex = (focusedIndex + 1) % options.length;
    } else if (direction === 'pageUp') {
      nextIndex = Math.max(focusedIndex - pageSize, 0);
    } else if (direction === 'pageDown') {
      nextIndex = Math.min(focusedIndex + pageSize, options.length - 1);
    } else if (direction === 'last') {
      nextIndex = options.length - 1;
    }
    this.setState({
      focusedOption: options[nextIndex]
    });
  };

  private selectOption = (option: InputOptionAttributes) => {
    this.props.onSelect(option);
    this.props.onMenuClose();
    this.setState({ focusedOption: undefined });
    this.blurInput();
  };

  private hoverOption = (focusedOption: InputOptionAttributes) => {
    if (this.state.focusedOption === focusedOption) {
      return;
    }
    this.setState({ focusedOption });
  };

  private onMouseDown = (e: MouseEvent<Element>) => {
    if (e.button !== 0) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    this.focusInput();
  };

  private onScroll = () => {
    if (this.props.isMenuOpen) {
      this.props.onMenuClose();
    }
  };

  private setComponents = (components?: SelectComponents) => {
    this.components = defaultComponents({ components });
  };
}
